import React, { useEffect, useState } from 'react';
import Typography from '../../Components/DesignSystem/Typography/Typography';
import Button from '../../Components/DesignSystem/Button/Button';
import { ComponentDoc, PropDoc, docsForSection } from './componentDocs';
import { FoundationChoice, foundationDocFor } from './foundationDocs';

/**
 * The reference half of a showcase section: what the component is, when to reach for it,
 * what to reach for instead, its props, and a usage you can paste.
 *
 * It sits ABOVE the exhibits, never replacing them — the live component answers "what does
 * it look like", this answers "should I use it, and how". The page had only ever had the
 * first half, which is why every question about the second one was answered by opening
 * `src/types/DesignSystem/`.
 *
 * The copy lives in `componentDocs.ts` as data; this file is only its rendering. That
 * split is what lets `componentDocs.test.tsx` check the docs against the type files —
 * prose embedded in JSX can only be spellchecked by eye.
 */

/** `IconButton` → `icon-button`, for a stable testid per component. */
export const docSlug = (name: string): string =>
  name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const CodeWell = ({
  label, code, testid,
}: { label: string; code: string; testid: string }) => {
  const [copied, setCopied] = useState(false);

  /* The "Copied" label is a transient acknowledgement, so it clears itself — and cancels
     on unmount, because a setState after the section scrolls away is a React warning
     nobody would connect back to a copy button. */
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    /* Clipboard access is permission-gated and absent in jsdom. It failing is not an
       error worth surfacing: the text is selectable, which is the fallback anyway. */
    navigator.clipboard?.writeText(code).then(() => setCopied(true)).catch(() => {});
  };

  return (
    <div className="tw-rounded-srf tw-border tw-border-edge tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-surface-sunken">
        <Typography variant="sub-title-label" as="span" className="tw-uppercase tw-tracking-wider tw-text-ink-tertiary">
          {label}
        </Typography>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="tw-ml-auto"
          onClick={copy}
          data-testid={`${testid}-copy`}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre
        data-testid={testid}
        className="tw-bg-code-surface tw-text-code-ink tw-font-mono tw-p-3 tw-text-xs tw-overflow-auto tw-whitespace-pre tw-m-0"
      >
        {code}
      </pre>
    </div>
  );
};

/** The default cell — em dash rather than an empty cell, so a scan cannot lose its place. */
const orDash = (value?: string) => value ?? '—';

/**
 * **A plain `<table>`, deliberately — not the design-system `Table`.**
 *
 * Dogfooding the DS Table here was the first instinct and it was wrong, measurably. A
 * props table needs none of what that component is: no sorting, no sticky columns, no row
 * expansion, no loading or empty states, no scroll-aware chrome. What it does bring is a
 * `ResizeObserver` and a scroll measurement PER INSTANCE — and this page renders 51 of
 * them, one per documented component and foundation.
 *
 * Measured on the showcase's own suite, which renders the whole page once per test:
 * `DesignSystemShowcase.test.tsx` 26.5s -> 11.9s and `keyboard.test.tsx` 21.2s -> 8.7s
 * with the doc blocks out entirely. The CI shard holding these files went 8 to 12 minutes
 * and timed out a worker RPC. The cost is real for readers too: 51 ResizeObservers on
 * first paint of a reference page.
 *
 * The page already had this idiom — the keyboard-and-motion section is a plain `<table>`
 * on the same tokens. This matches it, and keeps every testid the DS version exposed.
 */
const TH = 'tw-px-3 tw-py-2 tw-text-title-header tw-text-ink tw-text-left';
const TD = 'tw-px-3 tw-py-2 tw-align-top';

const DocTable = ({
  headers, widths, rows,
}: {
  headers: string[];
  widths: (string | undefined)[];
  rows: React.ReactNode[];
}) => (
  <div className="tw-overflow-x-auto tw-overscroll-x-contain tw-rounded-srf tw-border tw-border-edge">
    <table className="tw-w-full tw-border-collapse">
      <thead className="tw-bg-surface-sunken">
        <tr>
          {headers.map((header, i) => (
            <th key={header} scope="col" className={TH} style={widths[i] ? { width: widths[i] } : undefined}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

const propRow = (testid: string, row: PropDoc) => (
  <tr key={row.name} className="tw-border-t tw-border-edge">
    <td className={TD}>
      <span data-testid={`${testid}-prop-${row.name}`}>
        <code className={`tw-text-sub-title-label ${row.deprecated ? 'tw-text-ink-tertiary tw-line-through' : 'tw-text-ink'}`}>
          {row.name}
        </code>
        {row.required && (
          <span className="tw-ml-1 tw-text-caption-metadata tw-text-tone-danger-glyph" title="required">*</span>
        )}
        {row.native && (
          <span className="tw-block tw-text-caption-metadata tw-text-ink-tertiary">native</span>
        )}
      </span>
    </td>
    <td className={TD}>
      <code className="tw-text-caption-metadata tw-text-ink-secondary tw-break-words">{row.type}</code>
    </td>
    <td className={TD}>
      {row.required
        ? <span className="tw-text-caption-metadata tw-text-ink-tertiary">required</span>
        : <code className="tw-text-caption-metadata tw-text-ink-secondary">{orDash(row.default)}</code>}
    </td>
    <td className={TD}>
      <span className="tw-text-body-primary tw-text-ink-secondary">
        {row.description}
        {row.deprecated && (
          <span className="tw-block tw-mt-1 tw-text-caption-metadata tw-text-tone-warning-ink">
            Deprecated — {row.deprecated}
          </span>
        )}
      </span>
    </td>
  </tr>
);

const DocCard = ({ doc }: { doc: ComponentDoc }) => {
  const slug = docSlug(doc.name);
  const testid = `showcase-doc-${slug}`;

  return (
    <div data-testid={testid} className="tw-mb-8 tw-space-y-4">
      <Typography
        variant="body-large"
        as="p"
        className="tw-max-w-[72ch] tw-text-ink"
        data-testid={`${testid}-summary`}
      >
        {doc.summary}
      </Typography>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
        <div className="tw-rounded-srf tw-border tw-border-edge tw-bg-surface-sunken tw-p-4">
          <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
            Use it when
          </Typography>
          <ul className="tw-list-disc tw-pl-5 tw-space-y-1" data-testid={`${testid}-use-when`}>
            {doc.useWhen.map((line) => (
              <li key={line} className="tw-text-body-primary tw-text-ink-secondary">{line}</li>
            ))}
          </ul>
        </div>

        <div className="tw-rounded-srf tw-border tw-border-edge tw-bg-surface-sunken tw-p-4">
          <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
            Reach for something else when
          </Typography>
          <ul className="tw-space-y-1" data-testid={`${testid}-instead-use`}>
            {doc.insteadUse.map(({ component, when }) => (
              <li key={component} className="tw-text-body-primary tw-text-ink-secondary">
                <strong className="tw-text-ink">{component}</strong>
                {' — '}
                {when}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <CodeWell
        label="Import"
        code={`import ${doc.name} from '${doc.importPath}';`}
        testid={`${testid}-import`}
      />

      {doc.props.length > 0 && (
        <div>
          <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
            Props
          </Typography>
          <div data-testid={`${testid}-props`}>
            <DocTable
              headers={['Prop', 'Type', 'Default', 'What it does']}
              widths={['20%', '26%', '12%', undefined]}
              rows={doc.props.map((prop) => propRow(testid, prop))}
            />
          </div>
        </div>
      )}

      <CodeWell label="Usage" code={doc.snippet} testid={`${testid}-snippet`} />
    </div>
  );
};

const choiceRow = (testid: string, row: FoundationChoice) => (
  <tr key={row.token} className="tw-border-t tw-border-edge">
    <td className={TD}>
      <span data-testid={`${testid}-choice-${row.token}`} className="tw-text-body-primary tw-text-ink">
        {row.job}
      </span>
    </td>
    <td className={TD}>
      <code className="tw-text-sub-title-label tw-text-ink tw-break-words">{row.token}</code>
    </td>
    <td className={TD}>
      <span className="tw-text-body-primary tw-text-ink-secondary">{row.when}</span>
    </td>
  </tr>
);

/**
 * The reference block for a section that documents VALUES rather than a component.
 *
 * Same three beats as a component card — what it is, how to choose, what to paste — with
 * the props table swapped for a `job -> token` decision table and the "reach for something
 * else" column swapped for prohibitions. A foundation has no alternative to point at; what
 * it has instead is a set of things that must never happen, and those were previously only
 * ever prose in the middle of a paragraph.
 */
export const FoundationBlock = ({ section }: { section: string }) => {
  const doc = foundationDocFor(section);
  if (!doc) return null;
  const testid = `showcase-foundation-${section}`;

  return (
    <div data-testid={testid} className="tw-mb-8 tw-space-y-4">
      <Typography
        variant="body-large"
        as="p"
        className="tw-max-w-[72ch] tw-text-ink"
        data-testid={`${testid}-summary`}
      >
        {doc.summary}
      </Typography>

      <div>
        <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
          How to choose
        </Typography>
        <div data-testid={`${testid}-choose`}>
          <DocTable
            headers={['When you need to…', 'Reach for', 'Why this one']}
            widths={['34%', '28%', undefined]}
            rows={doc.choose.map((choice) => choiceRow(testid, choice))}
          />
        </div>
      </div>

      <div className="tw-rounded-srf tw-border tw-border-edge tw-bg-surface-sunken tw-p-4">
        <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
          Never
        </Typography>
        <ul className="tw-list-disc tw-pl-5 tw-space-y-1" data-testid={`${testid}-never`}>
          {doc.never.map((rule) => (
            <li key={rule} className="tw-text-body-primary tw-text-ink-secondary">{rule}</li>
          ))}
        </ul>
      </div>

      <CodeWell label="Usage" code={doc.snippet} testid={`${testid}-snippet`} />
    </div>
  );
};

/**
 * Every component documented under one section. Sections that demo more than one —
 * Toggle/Checkbox/Radio, Tag/ChoiceChip, Dialog/SidePanel — get a card each, in
 * registry order.
 */
const DocBlock = ({ section }: { section: string }) => {
  const docs = docsForSection(section);
  if (docs.length === 0) return null;
  return (
    <div data-testid={`showcase-docs-${section}`}>
      {docs.map((doc) => <DocCard key={doc.name} doc={doc} />)}
    </div>
  );
};

export default DocBlock;
